<template>
  <v-dialog v-model="dialog" persistent max-width="680">
    <v-card v-if="Invoice" v-draggable-dialog class="transaction-dialog" :loading="loading">
      <div class="dialog-drag-handle transaction-dialog__header">
        <div class="d-flex align-center">
          <v-avatar size="42" color="green lighten-5" class="mr-3">
            <v-icon color="primary">mdi-credit-card-refresh-outline</v-icon>
          </v-avatar>
          <div>
            <div class="text-h6 font-weight-bold primary-text">Collection attempts</div>
            <div class="text-body-2 text--secondary">Invoice #{{ Invoice.localId }}</div>
          </div>
        </div>
        <div class="d-flex align-center">
          <v-icon small color="grey lighten-1" class="mr-2 drag-indicator">mdi-drag</v-icon>
          <v-btn icon aria-label="Close collection attempts" @click.stop="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </div>

      <v-divider />

      <v-card-text class="pa-0">
        <div class="transaction-summary">
          <div>
            <div class="summary-label">Invoice amount</div>
            <div class="summary-value">{{ Invoice.total | currency }}</div>
          </div>
          <div>
            <div class="summary-label">Collection history</div>
            <div class="summary-value">{{ attemptLabel }}</div>
          </div>
        </div>

        <div class="px-5 pt-5 pb-2 d-flex align-center justify-space-between">
          <div>
            <div class="subtitle-1 font-weight-bold primary-text">Payment activity</div>
            <div class="caption text--secondary">Select an attempt to see its full transaction details.</div>
          </div>
        </div>

        <div v-if="loading" class="pa-8 text-center text--secondary">
          Loading collection attempts…
        </div>

        <v-list v-else-if="Transactions.length" class="transaction-list px-3 pb-4">
          <template v-for="(row, index) in Transactions">
            <v-list-item :key="row.id || index" class="transaction-row" @click="transactionDetailDialogOpen(row)">
              <v-list-item-avatar size="38" :color="statusBackground(row.status)">
                <v-icon small :color="statusColor(row.status)">{{ statusIcon(row.status) }}</v-icon>
              </v-list-item-avatar>

              <v-list-item-content>
                <div class="d-flex flex-wrap align-center mb-1">
                  <span class="font-weight-bold primary-text mr-2">Attempt {{ index + 1 }}</span>
                  <v-chip x-small label :color="statusBackground(row.status)" :text-color="statusColor(row.status)"
                    class="font-weight-bold text-capitalize">
                    {{ row.status || "Unknown" }}
                  </v-chip>
                </div>
                <v-list-item-subtitle>
                  {{ row.createdAt | moment("MMM D, YYYY • h:mm A") }}
                  <span v-if="lastFour(row)"> · Card ending {{ lastFour(row) }}</span>
                </v-list-item-subtitle>
                <div v-if="errorMessage(row)" class="transaction-error mt-2">
                  {{ errorMessage(row) }}
                </div>
              </v-list-item-content>

              <v-list-item-action class="align-end ml-3">
                <div class="font-weight-bold primary-text">{{ row.amount | currency }}</div>
                <div class="caption text--secondary mt-1">{{ row.transactionId || "No transaction ID" }}</div>
                <v-icon small color="grey" class="mt-1">mdi-chevron-right</v-icon>
              </v-list-item-action>
            </v-list-item>
          </template>
        </v-list>

        <div v-else class="empty-state px-6 py-10 text-center">
          <v-avatar size="48" color="grey lighten-4" class="mb-3">
            <v-icon color="grey">mdi-history</v-icon>
          </v-avatar>
          <div class="font-weight-bold primary-text">No collection attempts yet</div>
          <div class="text-body-2 text--secondary mt-1">Payment activity will appear here.</div>
        </div>
      </v-card-text>
    </v-card>

    <TransactionDetailDialog
      :openTransactionDetail="openTransactionDetail"
      :selectedTransaction="selectedTransaction"
      @close="openTransactionDetail = false"
    />
  </v-dialog>
</template>

<script>
import TransactionDetailDialog from "@/modules/Invoice/components/TransactionDetail.vue";
import invoiceService from "@/modules/Invoice/service.js";

export default {
  name: "open-transaction",
  components: { TransactionDetailDialog },
  props: {
    openTransactionDialog: { default: false },
    selectedInvoice: { type: Object },
  },
  data() {
    return {
      loading: false,
      dialog: false,
      Invoice: null,
      openTransactionDetail: false,
      selectedTransaction: null,
      Transactions: [],
    };
  },
  computed: {
    attemptLabel() {
      const count = this.Transactions.length || Number(this.Invoice.transactionCount) || 0;
      return `${count} ${count === 1 ? "attempt" : "attempts"}`;
    },
  },
  watch: {
    openTransactionDialog(val) {
      this.dialog = val;
      if (!val || !this.selectedInvoice) return;

      this.Invoice = this.selectedInvoice;
      this.getTransaction();
    },
  },
  methods: {
    getTransaction() {
      this.loading = true;
      return invoiceService.getAllTransactionByInvoice(this.Invoice.id)
        .then(response => {
          this.Transactions = response.data || [];
          return response;
        })
        .catch(() => {
          this.Transactions = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    close() {
      this.dialog = false;
      this.empty();
      this.$emit("close");
    },
    empty() {
      this.Invoice = null;
      this.selectedTransaction = null;
      this.Transactions = [];
    },
    transactionDetailDialogOpen(transaction) {
      this.selectedTransaction = transaction;
      this.openTransactionDetail = true;
    },
    lastFour(transaction) {
      return transaction.metadata && transaction.metadata.last_4;
    },
    errorMessage(transaction) {
      return transaction.metadata && transaction.metadata.error_message;
    },
    statusIcon(status) {
      if (status === "settled") return "mdi-check";
      if (status === "voided" || status === "returned") return "mdi-close";
      return "mdi-clock-outline";
    },
    statusColor(status) {
      if (status === "settled") return "green darken-2";
      if (status === "voided" || status === "returned") return "red darken-2";
      return "blue-grey darken-2";
    },
    statusBackground(status) {
      if (status === "settled") return "green lighten-5";
      if (status === "voided" || status === "returned") return "red lighten-5";
      return "blue-grey lighten-5";
    },
  },
};
</script>

<style scoped>
.transaction-dialog {
  border-radius: 14px !important;
  overflow: hidden;
}

.transaction-dialog__header {
  min-height: 76px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: move;
  user-select: none;
}

.primary-text {
  color: #172033;
}

.drag-indicator {
  cursor: move;
}

.transaction-summary {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  padding: 18px 20px;
  background: #f6faf6;
  border-bottom: 1px solid #e3ebe3;
}

.summary-label {
  color: #758096;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.summary-value {
  margin-top: 3px;
  color: #172033;
  font-size: 1.15rem;
  font-weight: 700;
}

.transaction-row {
  margin-top: 8px;
  padding: 10px 12px;
  border: 1px solid #e4e9e4;
  border-radius: 10px;
}

.transaction-row:hover {
  background: #f7faf7;
}

.transaction-error {
  padding: 7px 9px;
  color: #a43b3b;
  background: #fff5f5;
  border-radius: 6px;
  font-size: 0.75rem;
}

@media (max-width: 600px) {
  .transaction-dialog__header {
    padding: 14px 16px;
  }

  .drag-indicator {
    display: none;
  }

  .transaction-summary {
    padding: 14px 16px;
  }

  .transaction-row {
    align-items: flex-start;
  }

  .transaction-row .v-list-item__avatar {
    display: none;
  }

  .transaction-row .v-list-item__action {
    min-width: 92px;
  }
}
</style>
